import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Copy, Link as LinkIcon, QrCode, Clock, Settings, X } from 'lucide-react';
import { FiEdit3, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../../shared/contexts/AuthContext';
import '../../styles/contents/shortFormQuiz/ShortFormQuizTeacherPage.css';
import { db, checkFirebaseConnection } from '../../shared/api/firebase';
import { doc, getDoc, onSnapshot, collection, getDocs, updateDoc, deleteDoc, writeBatch, serverTimestamp, query, where, setDoc, deleteField } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import QRModal from '../../components/Modals/QRModal';
import { getTeacherClasses } from '../../services/firebaseService';

const ShortFormQuizTeacherPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const location = useLocation();
  const { currentUser } = useAuth();

  // 스피너 애니메이션을 위한 CSS 스타일 추가
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [rowsToDisplay, setRowsToDisplay] = useState([]);
  const [starting, setStarting] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('started'); // 'idle' | 'started' - 기본값을 'started'로 설정
  const [totalStages, setTotalStages] = useState(5);
  const [perStageSeconds, setPerStageSeconds] = useState(30);
  const [hasSavedTime, setHasSavedTime] = useState(false);
  const [forceShowTimeSetup, setForceShowTimeSetup] = useState(false);
  const [savingTime, setSavingTime] = useState(false);
  const [includeTutorial, setIncludeTutorial] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [isMutating, setIsMutating] = useState(false);
  const [checkedItems, setCheckedItems] = useState([]);
  const [showQRCode, setShowQRCode] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showContentSettingsModal, setShowContentSettingsModal] = useState(false);
  // localStorage에서 학급 정보 가져오기
  const [classInfo, setClassInfo] = useState(null);
  const [classLoadError, setClassLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0); // 재시도 횟수 추적

  useEffect(() => {
    const loadClassData = async () => {
      try {
        const storedData = localStorage.getItem('shortFormQuiz_selectedClass') || 
                          localStorage.getItem('marbleDrawing_selectedClass');
        
        if (storedData) {
          const parsed = JSON.parse(storedData);
          const { classId } = parsed;
          setClassInfo({ classId });
        }
      } catch (error) {
        console.error('저장된 학급 데이터 파싱 실패:', error);
        setClassLoadError('저장된 학급 정보를 불러올 수 없습니다.');
      }
    };
    
    loadClassData();
  }, []);

  const classId = classInfo?.classId || null;
  const [selectedClass, setSelectedClass] = useState(null);
  const [isClassLoading, setIsClassLoading] = useState(false);
  const { isGuest } = useAuth();

  // 모바일 화면 감지를 위한 상태
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 모달 열릴 때 뒤 화면 스크롤 방지
  useEffect(() => {
    if (showContentSettingsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showContentSettingsModal]);

  const inviteUrl = useMemo(() => {
    if (!code) return '';
    const baseUrl = `${window.location.origin}/play/shortFormQuiz/${code}/student`;
    const params = new URLSearchParams(location.search);
    const classId = params.get('classId');

    if (classId) {
      return `${baseUrl}?classId=${classId}`;
    }
    return baseUrl;
  }, [code, location.search]);

  // 학급 정보 로드
  useEffect(() => {
    const loadClassInfo = async () => {
      if (currentUser?.uid && !isGuest && classId) {
        setIsClassLoading(true);
        try {
          const result = await getTeacherClasses(currentUser.uid);
          if (result.success) {
            const foundClass = result.data.find(cls => cls.id === classId);
            if (foundClass) {
              setSelectedClass(foundClass);
            }
          }
        } catch (error) {
          console.error('학급 정보 로드 실패:', error);
        } finally {
          setIsClassLoading(false);
        }
      }
    };
    loadClassInfo();
  }, [currentUser, isGuest, classId]);

  // Helper functions
  const getCollectionName = (currentUser) => {
    return currentUser ? 'shortForm_Quiz_member' : 'shortForm_Quiz_guest';
  };

  const generateUserId = (currentUser) => {
    if (currentUser) return currentUser.uid;
    const guestIdKey = 'guest_shortform_id';
    const existingGuestId = localStorage.getItem(guestIdKey);
    if (existingGuestId) return existingGuestId;
    const timestamp = new Date().getTime();
    const randomStr = Math.random().toString(36).substring(2, 10);
    const newGuestId = `guest_${timestamp}_${randomStr}`;
    localStorage.setItem(guestIdKey, newGuestId);
    return newGuestId;
  };

  const getStorageKey = (currentUser, classId = null) => {
    const baseKey = currentUser ? `shortForm_savedQuizzes_${currentUser.uid}` : 'shortForm_savedQuizzes_guest';
    return classId ? `${baseKey}_${classId}` : baseKey;
  };

  useEffect(() => {
    const load = async () => {
      if (!code) {
        setError('퀴즈 코드가 없습니다.');
        return;
      }
      setLoading(true);
      setError(null);
      
      try {
        let found = null;
        const [docId, uniqueQuizIdPart] = String(code).split('::');

        if (!docId) {
          throw new Error('잘못된 퀴즈 코드 형식입니다.');
        }

        const connection = await checkFirebaseConnection();
        if (connection && connection.overall && db) {
          
          const collectionsToTry = ['shortForm_Quiz_member', 'shortForm_Quiz_guest'];
          for (const collectionName of collectionsToTry) {
            try {
              const userDocRef = doc(db, collectionName, docId);
              const userDocSnap = await getDoc(userDocRef);

              if (userDocSnap.exists()) {
                const data = userDocSnap.data();
                const quizList = data.퀴즈List || [];
                const targetQuiz = quizList.find(q => q.id === code);

                if (targetQuiz) {
                  found = { 
                    ...targetQuiz, 
                    origin: collectionName === 'shortForm_Quiz_member' ? 'member' : 'guest' 
                  };
                  break; 
                }
              }
            } catch (error) {
              if (error.code === 'permission-denied') {
                continue;
              }
              throw error;
            }
          }
        }

        if (found) {
          setQuiz(found);
          setTimeout(() => autoStartGame(found), 500);
        } else {
          setError('퀴즈를 찾을 수 없습니다. 링크(코드)를 확인하세요.');
        }

      } catch (e) {
        setError('퀴즈를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [code]); 

  useEffect(() => {
    if (quiz) {
      try {
        const qLen = Array.isArray(quiz?.questions) ? quiz.questions.length : 0;
        setTotalStages(Math.max(1, 1 + qLen));
      } catch (_) {}
    }
  }, [quiz]);
    
  // 로컬 저장된 시간 설정 폴백 확인
  useEffect(() => {
    try {
      if (quiz?.id) {
        const key = `shortFormQuizTime_${String(quiz.id)}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          const num = Number(saved);
          if (!Number.isNaN(num) && num > 0) {
            setPerStageSeconds(num);
            setHasSavedTime(true);
          }
        }
      }
    } catch (_) {}
  }, [quiz?.id]);

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (_) {}
  };

  // 세션 상태/설정 및 모든 응답 데이터 실시간 구독 (하나의 리스너로 통합)
  useEffect(() => {
    let unsub = null;
    const listenSession = async () => {
      if (!quiz?.id) return;
      
      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, quiz.id);
      
      unsub = onSnapshot(sessionDocRef, (snap) => {
        if (snap.exists()) {
          const data = snap.data();

          // 세션 상태 및 설정 업데이트
          const st = data?.session?.status || 'started';
          setSessionStatus(st);
          const sec = data?.session?.perStageSeconds;
          if (typeof sec === 'number' && sec > 0) {
            setPerStageSeconds(sec);
            setHasSavedTime(true);
          }
          if (typeof data?.session?.includeTutorial === 'boolean') {
            setIncludeTutorial(data.session.includeTutorial);
          }
          
          // 학생 응답 데이터 처리
          const responsesMap = data.responses || {};
          const allResponses = Object.keys(responsesMap).map(studentId => {
            const responseData = responsesMap[studentId];
            return {
              ...responseData,
              _id: studentId,
              studentId: studentId,
            };
          });

          // 최신순 정렬 (제출 시간 > 참여 시간)
          allResponses.sort((a, b) => {
            const timeA = a.submittedAt?.toDate ? a.submittedAt.toDate().getTime() : (a.joinedAt?.toDate ? a.joinedAt.toDate().getTime() : 0);
            const timeB = b.submittedAt?.toDate ? b.submittedAt.toDate().getTime() : (b.joinedAt?.toDate ? b.joinedAt.toDate().getTime() : 0);
            return timeB - timeA;
          });

          setRowsToDisplay(allResponses);

        } else {
          // 문서가 없는 경우 (아직 아무도 참여 안 함)
          setRowsToDisplay([]);
        }
      });
    };
    listenSession();
    return () => { if (unsub) unsub(); };
  }, [quiz?.id, quiz?.origin]);

  // 퀴즈 로드 후 자동으로 콘텐츠 시작
  const autoStartGame = async (quizData) => {
    try {
      if (!quizData?.id) return;
      const docId = String(quizData.id);
      
      // Firestore 연결이 가능한 경우에만 자동 시작
      if (db) {
        try {
          // 실시간 세션용 개별 문서 생성/업데이트 (임시 문서)
          const sessionCollectionName = quizData.origin === 'guest' 
            ? 'shortForm_Quiz_sessions_guest' 
            : 'shortForm_Quiz_sessions_member';
          const sessionDocRef = doc(db, sessionCollectionName, docId);
          
          const computedTotalStages = Math.max(1, 1 + ((quizData?.questions?.length) || 0));
          
          // 세션 문서 생성/업데이트 (퀴즈 정보 포함)
          // undefined 필드는 제외하고 객체 생성
          const sessionData = {
            // 퀴즈 기본 정보
            id: quizData.id,
            accessCode: quizData.id,
            title: quizData.title || '제목 없음',
            questions: quizData.questions || [],
            meta: quizData.meta || {},
            ownerUid: currentUser?.uid || null,
            origin: currentUser ? 'member' : 'guest',
            
            // 세션 정보
            session: {
              status: 'started',
              startedAt: serverTimestamp(),
              totalStages: computedTotalStages,
              currentStage: 1,
              perStageSeconds: Number(perStageSeconds) || 30,
              mode: 'play',
              stageStartedAt: serverTimestamp(),
              includeTutorial: includeTutorial
            },
            
            // 메타데이터
            isTemporarySession: true, // 임시 세션임을 표시
            createdAt: quizData.createdAt || serverTimestamp(),
            updatedAt: serverTimestamp()
          };

          // undefined가 아닌 필드만 추가
          if (quizData.grade !== undefined) sessionData.grade = quizData.grade;
          if (quizData.subject !== undefined) sessionData.subject = quizData.subject;
          if (quizData.school !== undefined) sessionData.school = quizData.school;
          if (quizData.mode !== undefined) sessionData.mode = quizData.mode;
          if (quizData.topic !== undefined) sessionData.topic = quizData.topic;
          
          await setDoc(sessionDocRef, sessionData, { merge: true });
          
        } catch (error) {
          console.warn('세션 시작 실패:', error);
        }
      }
    } catch (error) {
      console.warn('autoStartGame 오류:', error);
    }
  };

  // 시간 설정 저장
  const saveTimeSetting = async () => {
    try {
      if (!quiz?.id) return;
      setSavingTime(true);
      const docId = String(quiz.id);
      const computedTotalStages = Math.max(1, 1 + ((quiz?.questions?.length) || 0));

      try {
        // 실시간 세션 문서 업데이트
        const sessionCollectionName = quiz.origin === 'guest' 
          ? 'shortForm_Quiz_sessions_guest' 
          : 'shortForm_Quiz_sessions_member';
        const sessionDocRef = doc(db, sessionCollectionName, docId);
        
        await updateDoc(sessionDocRef, {
          'session.perStageSeconds': Number(perStageSeconds) || 30,
          'session.totalStages': computedTotalStages,
          'session.status': sessionStatus || 'idle',
          'session.includeTutorial': includeTutorial,
          'updatedAt': serverTimestamp()
        });
      } catch (e) {
        console.warn('Firestore 시간 설정 저장 실패:', e);
      }

      // 항상 로컬에도 백업 저장
      try { 
        localStorage.setItem(`shortFormQuizTime_${docId}`, String(Number(perStageSeconds) || 30)); 
      } catch (_) {}

      setHasSavedTime(true);
      setForceShowTimeSetup(false);
    } catch (error) {
      console.error('시간 저장 오류:', error);
      alert('시간 저장 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSavingTime(false);
    }
  };

  const startTest = async () => {
    try {
      if (starting || !quiz?.id) return;
      setStarting(true);
      const docId = String(quiz.id);
      const computedTotalStages = Math.max(1, 1 + ((quiz?.questions?.length) || 0));
      
      // 실시간 세션 문서 생성/업데이트
      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, docId);
      
      // undefined 필드는 제외하고 객체 생성
      const sessionData = {
        // 퀴즈 기본 정보 (세션 시작 시 확실히 설정)
        id: quiz.id,
        accessCode: quiz.id,
        title: quiz.title || '제목 없음',
        questions: quiz.questions || [],
        meta: quiz.meta || {},
        ownerUid: currentUser?.uid || null,
        origin: currentUser ? 'member' : 'guest',
        
        // 세션 정보
        session: {
          status: 'started',
          startedAt: serverTimestamp(),
          totalStages: computedTotalStages,
          currentStage: 1,
          perStageSeconds: Number(perStageSeconds) || 30,
          mode: 'play',
          stageStartedAt: serverTimestamp(),
          includeTutorial: includeTutorial
        },
        
        // 메타데이터
        isTemporarySession: true,
        createdAt: quiz.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // undefined가 아닌 필드만 추가
      if (quiz.grade !== undefined) sessionData.grade = quiz.grade;
      if (quiz.subject !== undefined) sessionData.subject = quiz.subject;
      if (quiz.school !== undefined) sessionData.school = quiz.school;
      if (quiz.mode !== undefined) sessionData.mode = quiz.mode;
      if (quiz.topic !== undefined) sessionData.topic = quiz.topic;
      
      await setDoc(sessionDocRef, sessionData, { merge: true });
    } catch (error) {
      console.error('테스트 시작 오류:', error);
      alert('테스트 시작 중 오류가 발생했습니다.');
    } finally {
      setStarting(false);
    }
  };

  const endTest = async () => {
    try {
      if (!quiz?.id) return;
      const docId = String(quiz.id);
      
      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, docId);
      
      await updateDoc(sessionDocRef, {
        'session.status': 'idle',
        'updatedAt': serverTimestamp()
      });
    } catch (error) {
      console.error('테스트 종료 오류:', error);
      alert('테스트 종료 중 오류가 발생했습니다.');
    }
  };

  const totalQuestions = quiz?.questions?.length || 0;
  const gradeOf = (correct, total=totalQuestions) => {
    const pct = total > 0 ? (correct / total) * 100 : 0;
    if (pct >= 80) return 1; if (pct >= 50) return 2; return 3;
  };
  const stats = useMemo(() => {
    // 완료된 학생들만 통계에 포함 (진행중인 학생 제외)
    const completedStudents = rowsToDisplay.filter(r => {
      // 제출 완료되었거나 finished 상태인 학생들만 포함
      return (r.submittedAt || r.finished) && r.source !== 'par';
    });
    
    if (!completedStudents || completedStudents.length === 0 || totalQuestions === 0) {
      return { avgScore: 0, maxScore: 0, minScore: 0, countByGrade: { 1: 0, 2: 0, 3: 0 }, completedCount: 0 };
    }
    
    const scores = completedStudents.map(r => r.correct);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    const maxScore = Math.max(...scores);
    const minScore = Math.min(...scores);
    const countByGrade = { 1: 0, 2: 0, 3: 0 };
    scores.forEach(s => { countByGrade[gradeOf(s)] += 1; });
    
    return { 
      avgScore, 
      maxScore, 
      minScore, 
      countByGrade, 
      completedCount: completedStudents.length 
    };
  }, [rowsToDisplay, totalQuestions]);

  const openDetail = (resp) => {
    setSelectedResponse(resp);
    setShowDetail(true);
  };
  const closeDetail = () => { setShowDetail(false); setSelectedResponse(null); };
  const startEdit = (resp) => { setEditingId(resp._id); setEditingName(resp.name || ''); };
  const cancelEdit = () => { setEditingId(null); setEditingName(''); };

  const commitEdit = async (resp) => {
    const oldName = resp.name;
    const newName = editingName.trim();

    if (!newName || oldName === newName) {
      cancelEdit();
      return;
    }

    setIsMutating(true);
    try {
      const docId = String(quiz.id);
      const studentIdToEdit = resp.studentId;
      if (!studentIdToEdit) throw new Error('학생 ID가 없어 수정할 수 없습니다.');

      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, docId);

      // 점(.) 표기법을 사용하여 특정 학생의 이름만 업데이트
      await updateDoc(sessionDocRef, {
        [`responses.${studentIdToEdit}.name`]: newName,
        [`responses.${studentIdToEdit}.updatedAt`]: serverTimestamp()
      });
      
      cancelEdit();
    } catch (e) {
      console.error("Error updating name:", e);
      alert('이름 수정 중 오류가 발생했습니다.');
    } finally {
      setIsMutating(false);
    }
  };

  const deleteOne = async (resp) => {
    if (!window.confirm('해당 응답을 삭제하시겠습니까?')) return;
    setIsMutating(true);
    try {
      const docId = String(quiz.id);
      const studentIdToDelete = resp.studentId;
      if (!studentIdToDelete) throw new Error('학생 ID가 없어 삭제할 수 없습니다.');

      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, docId);

      // deleteField를 사용하여 map에서 특정 키-값 쌍을 제거
      await updateDoc(sessionDocRef, {
        [`responses.${studentIdToDelete}`]: deleteField()
      });

    } catch (e) {
      console.error('[삭제] 오류:', e);
      alert(`삭제 중 오류가 발생했습니다: ${e.message}`);
    } finally {
      setIsMutating(false);
    }
  };

  const handleAllChecked = (e) => {
    if (e.target.checked) {
      setCheckedItems(rowsToDisplay.map(r => r._id));
    } else {
      setCheckedItems([]);
    }
  };

  const handleSingleChecked = (e, id) => {
    if (e.target.checked) {
      setCheckedItems(prev => [...prev, id]);
    } else {
      setCheckedItems(prev => prev.filter(i => i !== id));
    }
  };

  const deleteSelected = async () => {
    if (checkedItems.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }
    if (!window.confirm(`${checkedItems.length}개의 항목을 삭제하시겠습니까?`)) return;

    setIsMutating(true);
    try {
      const docId = String(quiz.id);
      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, docId);

      const itemsToDelete = rowsToDisplay.filter(r => checkedItems.includes(r._id));
      
      const updates = {};
      itemsToDelete.forEach(item => {
        if (item.studentId) {
          updates[`responses.${item.studentId}`] = deleteField();
        }
      });

      if (Object.keys(updates).length > 0) {
        await updateDoc(sessionDocRef, updates);
      }

      setCheckedItems([]);
      alert('선택한 항목이 삭제되었습니다.');
    } catch (e) {
      console.error('[선택삭제] 오류:', e);
      alert(`삭제 중 오류가 발생했습니다: ${e.message}`);
    } finally {
      setIsMutating(false);
    }
  };


  const resetAll = async () => {
    if (!window.confirm('이 학급의 모든 학생 결과와 참가자 목록을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    setIsMutating(true);
    try {
      const docId = String(quiz.id);
      const sessionCollectionName = quiz.origin === 'guest' 
        ? 'shortForm_Quiz_sessions_guest' 
        : 'shortForm_Quiz_sessions_member';
      const sessionDocRef = doc(db, sessionCollectionName, docId);

      // responses 맵 자체를 빈 객체로 덮어쓰기
      await updateDoc(sessionDocRef, {
        responses: {}
      });

      alert('이 학급의 모든 학생 결과와 참가자 목록이 삭제되었습니다.');
    } catch (e) {
      alert('전체 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsMutating(false);
    }
  };

  if (loading) {
    return (
      <section className="content-start-section" style={{ minHeight: '100vh', paddingTop: '20px' }}>
        <div className="container" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 20px' }}>
          <div className="content-navigation">
            <button
              className="btn-ghost"
              onClick={() => navigate('/play/shortFormQuiz')}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--gray-300)' }}
            >
              <ArrowLeft size={16} />
              돌아가기
            </button>
          </div>
          <div className="tools-frame-content" style={{ textAlign: 'center' }}>
            {retryCount > 0 ? (
              <>
                <div>퀴즈를 불러오는 중...</div>
                <div style={{ fontSize: '14px', color: 'var(--gray-600)', marginTop: '8px' }}>
                  재시도 {retryCount}/3
                </div>
              </>
            ) : (
              '불러오는 중...'
            )}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="content-start-section" style={{ minHeight: '100vh', paddingTop: '20px' }}>
        <div className="container" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 20px' }}>
          <div className="content-navigation">
            <button
              className="btn-ghost"
              onClick={() => navigate('/play/shortFormQuiz')}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--gray-300)' }}
            >
              <ArrowLeft size={16} />
              돌아가기
            </button>
          </div>
          <div className="tools-frame-content" style={{ color: 'var(--danger, #dc3545)' }}>{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="content-start-section" style={{ minHeight: '100vh', paddingTop: '20px' }}>
      <div className="container" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 20px' }}>
        <div className="content-navigation">
          <button
            className="btn-ghost"
            onClick={() => navigate('/play/shortFormQuiz')}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--gray-300)' }}
          >
            <ArrowLeft size={16} />
            돌아가기
          </button>
        </div>

        <div className="tools-frame-content" style={{ 
          maxWidth: '1200px',
          width: '100%',
          margin: '0 auto',
          padding: isMobile ? '16px 12px 0' : '20px 24px 0'
        }}>
          {/* 상단 정보 */}
          <div style={{ 
            position: 'relative',
            marginBottom: 'var(--spacing-lg)'
          }}>
            {/* 모바일에서 설정 버튼을 오른쪽 상단에 위치 */}
            {isMobile && (
              <div style={{
                position: 'absolute',
                top: '0',
                right: '0',
                zIndex: 10
              }}>
                <button 
                  onClick={() => setShowContentSettingsModal(true)}
                  style={{
                    padding: '10px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #fbbf24',
                    backgroundColor: '#fbbf24',
                    color: '#000',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    fontWeight: '600',
                    minWidth: '44px',
                    minHeight: '44px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f59e0b';
                    e.currentTarget.style.borderColor = '#f59e0b';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fbbf24';
                    e.currentTarget.style.borderColor = '#fbbf24';
                  }}
                >
                  <Settings size={18} />
                </button>
              </div>
            )}
            
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              justifyContent: 'space-between', 
              gap: 'var(--spacing-md)', 
              flexWrap: 'wrap',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 'var(--spacing-lg)' : 'var(--spacing-xl)',
              paddingRight: isMobile ? '60px' : '0' // 모바일에서 설정 버튼 공간 확보
            }}>
              <div style={{ 
                display: 'grid', 
                gap: isMobile ? 'var(--spacing-sm)' : 'var(--spacing-xs)',
                flex: 1,
                minWidth: 0 
              }}>
                <h2 className="section-title" style={{ 
                  margin: 0,
                  marginTop: isMobile ? '12px' : '8px',
                  fontSize: isMobile ? '20px' : '24px',
                  lineHeight: '1.3',
                  wordBreak: 'break-word',
                  textAlign: 'left'
                }}>{quiz?.title || '제목 없음'}</h2>
                <p className="section-description" style={{ 
                  margin: 0,
                  fontSize: isMobile ? '14px' : '16px',
                  lineHeight: '1.4',
                  textAlign: 'left'
                }}>
                  {selectedClass && (
                    <span style={{ 
                      background: 'var(--primary-light)', 
                      color: 'var(--primary)', 
                      padding: isMobile ? '4px 8px' : '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: isMobile ? '12px' : 'var(--font-sm)',
                      fontWeight: '600',
                      marginRight: '8px',
                      display: 'inline-block',
                      marginBottom: isMobile ? '4px' : '0'
                    }}>
                      {selectedClass.name} {selectedClass.grade}학년
                    </span>
                  )}
                  <span style={{ 
                    display: isMobile ? 'block' : 'inline',
                    fontSize: isMobile ? '13px' : 'inherit'
                  }}>
                  {quiz?.meta?.grade ? `${quiz.meta.grade}학년 · ` : ''}
                  {quiz?.meta?.subject || ''}
                  {quiz?.questions ? ` · ${quiz.questions.length}문제` : ''}
                  </span>
                </p>
              </div>
              
              {/* 데스크톱에서만 설정 버튼 표시 */}
              {!isMobile && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  flexWrap: 'wrap'
                }}>
                  <button 
                    onClick={() => setShowContentSettingsModal(true)}
                    style={{
                      padding: '10px 20px',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      border: '1px solid #fbbf24',
                      backgroundColor: '#fbbf24',
                      color: '#000',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontWeight: '600'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f59e0b';
                      e.currentTarget.style.borderColor = '#f59e0b';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fbbf24';
                      e.currentTarget.style.borderColor = '#fbbf24';
                    }}
                  >
                    <Settings size={18} />
                    콘텐츠 설정
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 초대(좌측) + 응시 통계(우측) 섹션 */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 'var(--spacing-md)' : 'var(--spacing-xl)',
            alignItems: 'start',
            marginBottom: 'var(--spacing-sm)',
            maxWidth: 'none'
          }}>
            {/* 좌측: 초대 */}
            <div style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)',
              padding: isMobile ? 'var(--spacing-md)' : 'var(--spacing-lg)',
              background: '#fff',
              minHeight: '320px'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '12px' }}>초대</h3>
              {/* QR 먼저 표시 */}
              <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                <div
                  onClick={() => setShowQRModal(true)}
                  style={{ cursor: 'pointer', display: 'inline-block', transition: 'transform 0.15s ease' }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.03)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <QRCodeSVG 
                    value={inviteUrl} 
                    size={isMobile ? 140 : 180} 
                    level="M" 
                    includeMargin={true} 
                  />
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: 6, 
                    color: 'var(--gray-500)', 
                    marginTop: 8,
                    fontSize: isMobile ? '12px' : '14px'
                  }}>
                    <QrCode size={isMobile ? 14 : 16} /> 클릭하여 확대하기
                  </div>
                </div>
              </div>
              {/* 구분자 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 12px' }}>
                <div style={{ height: 1, background: 'var(--gray-200)', flex: 1 }} />
                <span style={{ color: 'var(--gray-500)', fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>또는</span>
                <div style={{ height: 1, background: 'var(--gray-200)', flex: 1 }} />
              </div>
              {/* 링크 복사 버튼 (뒤에 표시) */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={copyInviteLink}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 8 : 10,
                    padding: isMobile ? '12px 20px' : '14px 28px',
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 600,
                    backgroundColor: copied ? '#28a745' : '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
                    minWidth: isMobile ? 120 : 140,
                    justifyContent: 'center',
                    width: isMobile ? '100%' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (!copied) e.currentTarget.style.backgroundColor = '#0056b3';
                  }}
                  onMouseLeave={(e) => {
                    if (!copied) e.currentTarget.style.backgroundColor = '#007bff';
                  }}
                >
                  <Copy size={isMobile ? 16 : 18} /> {copied ? '복사됨' : '링크 복사'}
                </button>
              </div>
            </div>

            {/* 우측: 응시 통계 */}
            <div style={{
              border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius-lg)',
              padding: isMobile ? 'var(--spacing-md)' : 'var(--spacing-lg)',
              background: '#fff',
              minHeight: '320px'
            }}>
                <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                marginBottom: '8px',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '8px' : '0'
              }}>
                <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px' }}>응시 통계</h3>
                <div style={{ 
                  fontSize: isMobile ? '12px' : '14px', 
                  color: 'var(--gray-600)',
                  background: 'var(--gray-100)',
                  padding: isMobile ? '3px 6px' : '4px 8px',
                  borderRadius: '12px',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}>
                  완료: {stats.completedCount}명 / 전체: {rowsToDisplay.length}명
                </div>
              </div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)', 
                gap: isMobile ? 8 : 16, 
                textAlign: 'center' 
              }}>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 20 : 28, 
                    fontWeight: 800 
                  }}>{stats.completedCount > 0 ? Math.round(stats.avgScore * 10) / 10 : 0}</div>
                  <div style={{ 
                    color: 'var(--gray-500)', 
                    fontSize: isMobile ? '12px' : '14px' 
                  }}>평균 점수</div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 20 : 28, 
                    fontWeight: 800 
                  }}>{stats.completedCount > 0 ? stats.maxScore : 0}</div>
                  <div style={{ 
                    color: 'var(--gray-500)', 
                    fontSize: isMobile ? '12px' : '14px' 
                  }}>최고 점수</div>
                </div>
                <div>
                  <div style={{ 
                    fontSize: isMobile ? 20 : 28, 
                    fontWeight: 800 
                  }}>{stats.completedCount > 0 ? stats.minScore : 0}</div>
                  <div style={{ 
                    color: 'var(--gray-500)', 
                    fontSize: isMobile ? '12px' : '14px' 
                  }}>최저 점수</div>
                </div>
              </div>
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? 8 : 16, 
                justifyContent: 'center', 
                marginTop: 12 
              }}>
                {[1,2,3].map(g => (
                  <div key={g} style={{ display: 'grid', justifyItems: 'center' }}>
                    <div style={{
                      width: isMobile ? 36 : 48,
                      height: isMobile ? 36 : 48,
                      borderRadius: '50%',
                      background: g === 1 ? 'var(--primary)' : g === 2 ? '#10b981' : '#64748b',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: isMobile ? '14px' : '16px'
                    }}>{g}</div>
                    <div style={{ 
                      marginTop: 6, 
                      fontSize: isMobile ? 14 : 18, 
                      fontWeight: 700 
                    }}>{stats.countByGrade[g]}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 학생 결과 테이블 */}
          <div style={{
            marginTop: 'var(--spacing-sm)',
            border: '1px solid var(--gray-200)',
            borderRadius: 'var(--radius-lg)',
            padding: isMobile ? 'var(--spacing-md)' : 'var(--spacing-lg)',
            background: '#fff'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'flex-start' : 'center', 
              marginBottom: 12,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? '12px' : '0'
            }}>
              <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '18px' }}>학생 결과</h3>
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? 8 : 12, 
                alignItems: 'center',
                width: isMobile ? '100%' : 'auto',
                flexDirection: 'row' // 모바일에서도 가로로 배치
              }}>
                <button 
                  onClick={deleteSelected} 
                  disabled={isMutating || checkedItems.length === 0}
                  style={{
                    padding: isMobile ? '12px 8px' : '10px 20px',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (checkedItems.length > 0 && !isMutating) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    background: isMutating ? '#6c757d' : (checkedItems.length > 0 ? '#ffc107' : '#e9ecef'),
                    color: isMutating ? '#fff' : (checkedItems.length > 0 ? '#000' : '#6c757d'),
                    opacity: (isMutating || checkedItems.length === 0) ? 0.7 : 1,
                    minWidth: isMobile ? '80px' : '120px',
                    height: isMobile ? '44px' : '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: isMobile ? 'calc(50% - 4px)' : 'auto',
                    flex: isMobile ? '1' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (checkedItems.length > 0 && !isMutating) {
                      e.currentTarget.style.background = '#e0a800';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (checkedItems.length > 0 && !isMutating) {
                      e.currentTarget.style.background = '#ffc107';
                    }
                  }}
                >
                  {isMutating && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid #fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {isMutating ? '삭제 중...' : '선택 삭제'}
                </button>
                <button 
                  onClick={resetAll} 
                  disabled={isMutating}
                  style={{
                    padding: isMobile ? '12px 8px' : '10px 20px',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '600',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isMutating ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    background: isMutating ? '#6c757d' : '#dc3545',
                    color: '#fff',
                    opacity: isMutating ? 0.7 : 1,
                    minWidth: isMobile ? '80px' : '120px',
                    height: isMobile ? '44px' : '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    width: isMobile ? 'calc(50% - 4px)' : 'auto',
                    flex: isMobile ? '1' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (!isMutating) {
                      e.currentTarget.style.background = '#c82333';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMutating) {
                      e.currentTarget.style.background = '#dc3545';
                    }
                  }}
                >
                  {isMutating && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid #fff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {isMutating ? '삭제 중...' : '전체 삭제'}
                </button>
              </div>
            </div>
            {rowsToDisplay.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--gray-500)' }}>아직 제출된 답안이 없습니다.</div>
            ) : isMobile ? (
              // 모바일용 카드 레이아웃
              <div>
                {/* 전체 선택 체크박스 */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'var(--gray-50)',
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)'
                }}>
                  <input 
                    type="checkbox" 
                    onChange={handleAllChecked}
                    checked={checkedItems.length > 0 && checkedItems.length === rowsToDisplay.length}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-700)' }}>
                    전체 선택 ({checkedItems.length}/{rowsToDisplay.length})
                  </span>
                </div>
                
                {/* 학생 카드들 */}
                <div style={{ display: 'grid', gap: '12px' }}>
                  {rowsToDisplay.map((r, idx) => (
                    <div key={r._id} style={{
                      border: '1px solid var(--gray-200)',
                      borderRadius: '12px',
                      padding: '16px',
                      background: '#fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                    }}>
                      {/* 카드 헤더 */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <input 
                            type="checkbox" 
                            onChange={(e) => handleSingleChecked(e, r._id)}
                            checked={checkedItems.includes(r._id)}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <div style={{ 
                            background: 'var(--primary)', 
                            color: '#fff', 
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '700'
                          }}>
                            {idx + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {editingId === r._id ? (
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input 
                                  value={editingName} 
                                  onChange={(e) => setEditingName(e.target.value)} 
                                  style={{ 
                                    border: '1px solid var(--gray-300)', 
                                    borderRadius: 6, 
                                    padding: '8px 10px',
                                    fontSize: '14px',
                                    flex: 1,
                                    minWidth: '100px'
                                  }} 
                                />
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button
                                    onClick={() => commitEdit(r)}
                                    disabled={isMutating || !editingName.trim()}
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: 12,
                                      border: '1px solid #198754',
                                      color: '#198754',
                                      background: 'transparent',
                                      borderRadius: 4,
                                      cursor: 'pointer',
                                      opacity: (isMutating || !editingName.trim()) ? 0.6 : 1
                                    }}
                                  >
                                    저장
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    disabled={isMutating}
                                    style={{
                                      padding: '6px 12px',
                                      fontSize: 12,
                                      border: '1px solid #6c757d',
                                      color: '#6c757d',
                                      background: 'transparent',
                                      borderRadius: 4,
                                      cursor: 'pointer',
                                      opacity: isMutating ? 0.6 : 1
                                    }}
                                  >
                                    취소
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={{ 
                                fontSize: '16px', 
                                fontWeight: '600', 
                                color: 'var(--gray-800)',
                                wordBreak: 'break-word'
                              }}>
                                {r.name || '-'}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* 상태 뱃지 */}
                        <span style={{
                          padding: '4px 8px', 
                          borderRadius: 12,
                          background: (r.submittedAt || r.finished) ? '#dcfce7' : (r.source === 'par' ? 'var(--gray-100)' : 'var(--primary-light)'),
                          color: (r.submittedAt || r.finished) ? '#059669' : (r.source === 'par' ? 'var(--gray-700)' : 'var(--primary)'),
                          fontSize: 11, 
                          fontWeight: 700,
                          whiteSpace: 'nowrap'
                        }}>
                          {(r.submittedAt || r.finished) ? '완료' : (r.source === 'par' ? '진행중' : '참여 완료')}
                        </span>
                      </div>
                      
                      {/* 점수 정보 */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '12px',
                        marginBottom: '12px',
                        padding: '12px',
                        background: 'var(--gray-50)',
                        borderRadius: '8px'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary)' }}>
                            {r.correct}/{totalQuestions}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--gray-600)' }}>점수</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary)' }}>
                            {gradeOf(r.correct, totalQuestions)}등급
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--gray-600)' }}>등급</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: (r.wrongIndexes || []).length > 0 ? '#dc3545' : 'var(--gray-500)'
                          }}>
                            {(r.wrongIndexes || []).length > 0 ? r.wrongIndexes.join(', ') : (r.source === 'par' ? '-' : '없음')}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--gray-600)' }}>틀린 문제</div>
                        </div>
                      </div>
                      
                      {/* 액션 버튼들 */}
                      <div style={{ 
                        display: 'flex', 
                        gap: 8, 
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end'
                      }}>
                        {/* 제출된 경우만 보기 버튼 노출 */}
                        {(r.submittedAt || r.source !== 'par') && (
                          <button
                            onClick={() => openDetail(r)}
                            style={{
                              padding: '8px 12px',
                              fontSize: 12,
                              border: '1px solid #0d6efd',
                              color: '#0d6efd',
                              background: 'transparent',
                              borderRadius: 6,
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            상세보기
                          </button>
                        )}
                        
                        {editingId === r._id ? null : (
                          <button
                            onClick={() => startEdit(r)}
                            disabled={isMutating}
                            style={{
                              padding: '8px 12px',
                              fontSize: 12,
                              border: '1px solid #0d6efd',
                              color: '#0d6efd',
                              background: 'transparent',
                              borderRadius: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              opacity: isMutating ? 0.6 : 1,
                              fontWeight: '500'
                            }}
                          >
                            <FiEdit3 size={12} /> 편집
                          </button>
                        )}
                        
                        <button
                          onClick={() => deleteOne(r)}
                          disabled={isMutating}
                          style={{
                            padding: '8px 12px',
                            fontSize: 12,
                            border: '1px solid #dc3545',
                            color: '#dc3545',
                            background: 'transparent',
                            borderRadius: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            opacity: isMutating ? 0.6 : 1,
                            fontWeight: '500'
                          }}
                        >
                          <FiTrash2 size={12} /> 삭제
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // 데스크톱용 테이블
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)' }}>
                      <th style={{ padding: '8px', borderBottom: '1px solid var(--gray-200)' }}>
                        <input 
                          type="checkbox" 
                          onChange={handleAllChecked}
                          checked={checkedItems.length > 0 && checkedItems.length === rowsToDisplay.length}
                        />
                      </th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--gray-200)' }}>#</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--gray-200)' }}>이름</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--gray-200)' }}>점수</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--gray-200)' }}>등급</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--gray-200)' }}>틀린 문제</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid var(--gray-200)' }}>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsToDisplay.map((r, idx) => (
                      <tr key={r._id}>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>
                          <input 
                            type="checkbox" 
                            onChange={(e) => handleSingleChecked(e, r._id)}
                            checked={checkedItems.includes(r._id)}
                          />
                        </td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>{idx + 1}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>
                          {editingId === r._id ? (
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input value={editingName} onChange={(e) => setEditingName(e.target.value)} style={{ border: '1px solid var(--gray-300)', borderRadius: 6, padding: '6px 8px' }} />
                              <button
                                onClick={() => commitEdit(r)}
                                disabled={isMutating || !editingName.trim()}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  border: '1px solid #198754',
                                  color: '#198754',
                                  background: 'transparent',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  opacity: (isMutating || !editingName.trim()) ? 0.6 : 1
                                }}
                              >
                                저장
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={isMutating}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  border: '1px solid #6c757d',
                                  color: '#6c757d',
                                  background: 'transparent',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  opacity: isMutating ? 0.6 : 1
                                }}
                              >
                                취소
                              </button>
                            </div>
                          ) : (
                            r.name || '-'
                          )}
                        </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>{r.correct}/{totalQuestions}</td>
                    <td style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>{gradeOf(r.correct, totalQuestions)}등급</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>{(r.wrongIndexes || []).length > 0 ? r.wrongIndexes.join(', ') : (r.source === 'par' ? '-' : '없음')}</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid var(--gray-100)' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {/* 상태 뱃지: 참여/진행/종료 */}
                            <span style={{
                              padding: '4px 10px', borderRadius: 999,
                              background: (r.submittedAt || r.finished) ? '#dcfce7' : (r.source === 'par' ? 'var(--gray-100)' : 'var(--primary-light)'),
                              color: (r.submittedAt || r.finished) ? '#059669' : (r.source === 'par' ? 'var(--gray-700)' : 'var(--primary)'),
                              fontSize: 12, fontWeight: 800
                            }}>
                              {(r.submittedAt || r.finished) ? '완료' : (r.source === 'par' ? '진행중' : '참여 완료')}
                            </span>
                            {/* 제출된 경우만 보기 버튼 노출 */}
                            {(r.submittedAt || r.source !== 'par') && (
                              <button
                                onClick={() => openDetail(r)}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  border: '1px solid #0d6efd',
                                  color: '#0d6efd',
                                  background: 'transparent',
                                  borderRadius: 4,
                                  cursor: 'pointer'
                                }}
                              >
                                보기
                              </button>
                            )}
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                              {editingId === r._id ? null : (
                                <button
                                  onClick={() => startEdit(r)}
                                  disabled={isMutating}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: 12,
                                    border: '1px solid #0d6efd',
                                    color: '#0d6efd',
                                    background: 'transparent',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    opacity: isMutating ? 0.6 : 1
                                  }}
                                >
                                  <FiEdit3 size={12} /> 편집
                                </button>
                              )}
                              <button
                                onClick={() => deleteOne(r)}
                                disabled={isMutating}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: 12,
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  background: 'transparent',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  opacity: isMutating ? 0.6 : 1
                                }}
                              >
                                <FiTrash2 size={12} /> 삭제
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QR 코드 모달 */}
      <QRModal
        show={showQRModal}
        onHide={() => setShowQRModal(false)}
        qrValue={inviteUrl}
        title="QR 코드"
        description="학생들이 QR 코드를 스캔하여 퀴즈에 참여할 수 있습니다."
        qrSize={640}
        qrLevel="H"
      />

      {/* 학생 답안 상세 모달 (간단 구현) */}
      {showDetail && selectedResponse && (
        <div
          style={{
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%',
            background: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            justifyContent: 'center', 
            zIndex: 1050,
            padding: isMobile ? '0' : '20px',
            overflow: 'auto'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) closeDetail(); }}
        >
          <div style={{ 
            background: '#fff', 
            borderRadius: isMobile ? '12px 12px 0 0' : 12, 
            width: isMobile ? '100%' : 'min(980px, 94%)', 
            maxHeight: isMobile ? '100%' : '88%', 
            height: isMobile ? '100%' : 'auto',
            overflow: 'auto', 
            padding: isMobile ? '16px' : 20,
            marginTop: isMobile ? '0' : 'auto',
            position: isMobile ? 'relative' : 'static'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: isMobile ? '16px' : '12px',
              position: isMobile ? 'sticky' : 'static',
              top: isMobile ? '0' : 'auto',
              background: '#fff',
              zIndex: 10,
              paddingBottom: isMobile ? '12px' : '0',
              borderBottom: isMobile ? '1px solid var(--gray-200)' : 'none'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: isMobile ? '18px' : '20px',
                wordBreak: 'break-word',
                flex: 1,
                paddingRight: '12px'
              }}>{selectedResponse.name}의 답안</h3>
              <button 
                className="btn-ghost" 
                onClick={closeDetail}
                style={{
                  padding: isMobile ? '8px 12px' : '6px 12px',
                  fontSize: isMobile ? '14px' : '16px',
                  minWidth: isMobile ? '60px' : 'auto'
                }}
              >
                닫기
              </button>
            </div>
            {(() => {
              const questions = quiz?.questions || [];
              const answers = selectedResponse.answers || [];
              const wrongIdxs = selectedResponse.wrongIndexes || [];
              const total = questions.length;
              const correctCount = selectedResponse.correct; // 재계산 대신 저장된 값 사용
              const pct = total > 0 ? Math.round((correctCount / total) * 100) : 0;
              const grade = (pct >= 80) ? 1 : (pct >= 50) ? 2 : 3;
              const submittedAt = selectedResponse.submittedAt?.toDate ? selectedResponse.submittedAt.toDate() : (selectedResponse.submittedAt || null);
              const label = (i) => ['A','B','C','D'][i] || String(i+1);
              return (
                <div style={{ marginTop: isMobile ? 8 : 12 }}>
                  {/* 요약 */}
                  <div style={{ 
                    border: '1px solid var(--gray-200)', 
                    borderRadius: 8, 
                    padding: isMobile ? 10 : 12, 
                    background: 'var(--gray-50)' 
                  }}>
                    <div style={{ 
                      display: 'grid',
                      gap: isMobile ? 8 : 16, 
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                      alignItems: 'center'
                    }}>
                      <div style={{ fontSize: isMobile ? '14px' : '16px' }}>
                        맞은 문제: <strong>{correctCount}</strong> / {total}개
                      </div>
                      <div style={{ fontSize: isMobile ? '14px' : '16px' }}>
                        등급: <span style={{ fontWeight: 800 }}>{grade}등급</span>
                      </div>
                      {submittedAt && (
                        <div style={{ 
                          color: 'var(--gray-600)',
                          fontSize: isMobile ? '12px' : '14px',
                          gridColumn: isMobile ? '1 / -1' : 'auto'
                        }}>
                          제출 시간: {new Date(submittedAt).toLocaleString()}
                        </div>
                      )}
                      {wrongIdxs?.length > 0 && (
                        <div style={{ 
                          color: 'var(--gray-700)',
                          fontSize: isMobile ? '12px' : '14px',
                          gridColumn: isMobile ? '1 / -1' : 'auto'
                        }}>
                          틀린 문제: {wrongIdxs.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* 문항 상세 */}
                  <div style={{ 
                    marginTop: isMobile ? 10 : 12, 
                    display: 'grid', 
                    gap: isMobile ? 10 : 12 
                  }}>
                    {questions.map((q, idx) => {
                      const selected = answers[idx];
                      const isWrong = wrongIdxs.includes(idx + 1) || (typeof selected === 'number' && selected !== q?.answerIndex);
                      return (
                        <div key={idx} style={{ 
                          border: '1px solid var(--gray-200)', 
                          borderRadius: 10, 
                          padding: isMobile ? 12 : 14, 
                          background: isWrong ? 'rgba(239,68,68,0.06)' : 'rgba(16,185,129,0.05)' 
                        }}>
                          <div style={{ 
                            fontWeight: 800, 
                            marginBottom: 6,
                            fontSize: isMobile ? '14px' : '16px' 
                          }}>문제 {idx + 1}</div>
                          {q?.question && (
                            <div style={{ 
                              marginBottom: 10,
                              fontSize: isMobile ? '14px' : '16px',
                              lineHeight: '1.5'
                            }}>{q.question}</div>
                          )}
                          <div style={{ display: 'grid', gap: isMobile ? 4 : 6 }}>
                            {(q?.options || []).map((opt, i) => {
                              const isCorrect = i === q?.answerIndex;
                              const isSelected = i === selected;
                              const bg = isCorrect ? 'rgba(16,185,129,0.15)' : (isSelected ? 'rgba(239,68,68,0.12)' : 'transparent');
                              const border = isCorrect ? '1px solid #10b981' : (isSelected ? '1px solid #ef4444' : '1px solid var(--gray-200)');
                              return (
                                <div key={i} style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: isMobile ? 8 : 10, 
                                  padding: isMobile ? '6px 8px' : '8px 10px', 
                                  borderRadius: 8, 
                                  background: bg, 
                                  border 
                                }}>
                                  <div style={{ 
                                    width: isMobile ? 20 : 22, 
                                    height: isMobile ? 20 : 22, 
                                    borderRadius: 999, 
                                    background: '#0ea5e9', 
                                    color: '#fff', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontSize: isMobile ? 10 : 12, 
                                    fontWeight: 800,
                                    flexShrink: 0
                                  }}>{label(i)}</div>
                                  <div style={{ 
                                    flex: 1,
                                    fontSize: isMobile ? '13px' : '14px',
                                    lineHeight: '1.4',
                                    wordBreak: 'break-word'
                                  }}>{opt}</div>
                                  <div style={{ 
                                    minWidth: isMobile ? 16 : 18, 
                                    textAlign: 'right',
                                    fontSize: isMobile ? '14px' : '16px',
                                    flexShrink: 0
                                  }}>{isCorrect ? '✓' : (isSelected ? '✗' : '')}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* 콘텐츠 설정 모달 */}
      {showContentSettingsModal && (
        <div
          style={{
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%',
            background: 'rgba(0,0,0,0.5)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            zIndex: 1050,
            padding: '20px'
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowContentSettingsModal(false); }}
        >
          <div style={{ 
            background: '#fff', 
            borderRadius: 12, 
            width: isMobile ? '90%' : 'min(600px, 90%)', 
            maxHeight: '80vh', 
            overflow: 'auto', 
            padding: isMobile ? '16px' : 24,
            position: 'relative',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            {/* 모달 헤더 */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '16px',
              borderBottom: '1px solid var(--gray-200)'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: isMobile ? '18px' : '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <Settings size={isMobile ? 20 : 24} />
                콘텐츠 설정
              </h3>
              <button 
                onClick={() => setShowContentSettingsModal(false)}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: '#dc3545',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  fontSize: '16px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#c82333';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc3545';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* 모달 내용 */}
            <div style={{ display: 'grid', gap: isMobile ? '20px' : '24px' }}>
              {/* 콘텐츠 활성화 섹션 */}
              <div style={{
                border: '1px solid var(--gray-200)',
                borderRadius: '12px',
                padding: isMobile ? '16px' : '20px',
                background: '#f8fafc'
              }}>
                <h4 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: isMobile ? '16px' : '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🎮 콘텐츠 활성화
                </h4>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'stretch' : 'center',
                  gap: isMobile ? '12px' : '16px'
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ 
                      margin: 0, 
                      fontSize: isMobile ? '14px' : '16px',
                      color: 'var(--gray-600)',
                      lineHeight: '1.5'
                    }}>
                      콘텐츠가 기본적으로 활성화되어 있어 학생들이 언제든지 퀴즈에 참여할 수 있습니다.
                    </p>
                  </div>
                  <div>
                    {sessionStatus === 'started' ? (
                      <button className="btn-danger" onClick={endTest} disabled={starting} style={{
                        padding: isMobile ? '12px 20px' : '10px 20px',
                        fontSize: isMobile ? '14px' : '16px',
                        width: isMobile ? '100%' : 'auto',
                        minHeight: isMobile ? '44px' : 'auto'
                      }}>
                        콘텐츠 비활성화
                      </button>
                    ) : (
                      <button className="btn-primary" onClick={startTest} disabled={starting} style={{
                        padding: isMobile ? '12px 20px' : '10px 20px',
                        fontSize: isMobile ? '14px' : '16px',
                        width: isMobile ? '100%' : 'auto',
                        minHeight: isMobile ? '44px' : 'auto'
                      }}>
                        {starting ? '시작 중...' : '콘텐츠 활성화'}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* 콘텐츠 설정 섹션 */}
              <div style={{
                border: '1px solid var(--gray-200)',
                borderRadius: '12px',
                padding: isMobile ? '16px' : '20px',
                background: '#fff'
              }}>
                <h4 style={{ 
                  margin: '0 0 16px 0', 
                  fontSize: isMobile ? '16px' : '18px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  ⚙️ 콘텐츠 설정
                </h4>

                {/* 문제당 시간 설정 */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  marginBottom: '16px',
                  padding: '12px 16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '12px' : '0'
                }}>
                  <label style={{ 
                    fontSize: isMobile ? '14px' : '16px', 
                    color: 'var(--gray-700)', 
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ 
                      width: '20px', 
                      height: '20px', 
                      borderRadius: '50%', 
                      background: '#3b82f6', 
                      color: '#fff', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>⏱</span>
                    문제당 시간
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMobile ? 'center' : 'flex-end', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        min={5}
                        max={100}
                        step={5}
                        value={perStageSeconds}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (!isNaN(value) && value >= 0) {
                            setPerStageSeconds(value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = Number(e.target.value);
                          if (isNaN(value) || value < 5 || value > 100 || value % 5 !== 0) {
                            const lastValidValue = Math.max(5, Math.min(100, Math.round(perStageSeconds / 5) * 5));
                            setPerStageSeconds(lastValidValue);
                          } else {
                            setPerStageSeconds(value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                        style={{ 
                          width: '80px', 
                          border: '1px solid var(--gray-300)', 
                          borderRadius: '6px', 
                          padding: '8px 12px',
                          textAlign: 'center',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: 'var(--gray-600)', fontWeight: '500' }}>초</span>
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: 'var(--gray-500)', 
                      textAlign: isMobile ? 'center' : 'right',
                      lineHeight: '1.2'
                    }}>
                      5초 단위 (5~100초)
                    </div>
                  </div>
                </div>

                {/* 튜토리얼 설정 */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid var(--gray-200)',
                  marginBottom: '20px',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '12px' : '0'
                }}>
                  <label htmlFor="includeTutorialModal" style={{ 
                    fontSize: isMobile ? '14px' : '16px', 
                    color: 'var(--gray-700)', 
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ 
                      width: '20px', 
                      height: '20px', 
                      borderRadius: '50%', 
                      background: '#10b981', 
                      color: '#fff', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>🎮</span>
                    튜토리얼 콘텐츠 포함
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="checkbox"
                      id="includeTutorialModal"
                      checked={includeTutorial}
                      onChange={(e) => setIncludeTutorial(e.target.checked)}
                      style={{ 
                        appearance: 'none',
                        width: '44px', 
                        height: '24px',
                        background: includeTutorial ? '#10b981' : '#d1d5db',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative'
                      }}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: includeTutorial ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      background: '#fff',
                      borderRadius: '50%',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      pointerEvents: 'none'
                    }} />
                  </div>
                </div>

                {/* 저장 버튼 */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button 
                    className="btn-primary" 
                    onClick={saveTimeSetting} 
                    disabled={savingTime}
                    style={{
                      padding: isMobile ? '12px 24px' : '10px 24px',
                      fontSize: isMobile ? '14px' : '16px',
                      minWidth: isMobile ? '150px' : '120px'
                    }}
                  >
                    {savingTime ? '저장 중...' : '설정 저장'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ShortFormQuizTeacherPage;



